'use client';

import * as React from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Bell as BellIcon } from '@phosphor-icons/react/dist/ssr/Bell';
import { List as ListIcon } from '@phosphor-icons/react/dist/ssr/List';
import { MagnifyingGlass as SearchIcon } from '@phosphor-icons/react/dist/ssr/MagnifyingGlass';

import type { NavItemConfig } from '@/types/v2/nav';
import { usePopover } from '@/hooks/use-popover';
import { useNotifications } from '@/hooks/v2/use-notifications';
import { useUser } from '@/hooks/v2/use-user';
import { UserAvatar } from '@/components/common/user-avatar';
import { SearchDialog } from '@/layouts/v2/search-dialog';

import { MobileNav } from '../mobile-nav';
import { NotificationsPopover } from '../notifications-popover';
import { UserPopover } from '../user-popover/user-popover';
import { ContextualHelpButton } from './contextual-help-button';

export interface MainNavProps {
  items: NavItemConfig[];
  bottomItems?: NavItemConfig[];
}

export function MainNav({ items, bottomItems }: MainNavProps): React.JSX.Element {
  const [openNav, setOpenNav] = React.useState<boolean>(false);
  const [searchOpen, setSearchOpen] = React.useState<boolean>(false);

  // Cmd+K / Ctrl+K to open search
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <React.Fragment>
      <Box
        component="header"
        sx={{
          bgcolor: 'var(--mui-palette-background-paper)',
          borderBottom: '1px solid var(--mui-palette-divider)',
          left: 0,
          position: 'sticky',
          pt: { lg: 'var(--Layout-gap)' },
          top: 0,
          width: '100%',
          zIndex: 'var(--MainNav-zIndex)',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            minHeight: 'var(--MainNav-height)',
            px: { xs: 2, lg: 3 },
            py: 1,
          }}
        >
          {/* Left — mobile menu toggle + search */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}>
            <IconButton
              onClick={() => setOpenNav(true)}
              size="small"
              sx={{ display: { lg: 'none' }, color: 'text.secondary' }}
            >
              <ListIcon />
            </IconButton>

            <Tooltip title="Search (⌘K)">
              <Box
                onClick={() => setSearchOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  transition: 'border-color 150ms, background-color 150ms',
                  '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                }}
              >
                <SearchIcon size={15} />
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', display: { xs: 'none', md: 'block' } }}>
                  Search…
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: 'text.disabled',
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.75,
                    px: 0.6,
                    py: 0.1,
                    fontFamily: 'monospace',
                    display: { xs: 'none', lg: 'block' },
                  }}
                >
                  ⌘K
                </Typography>
              </Box>
            </Tooltip>
          </Stack>

          {/* Right — help + notifications + user */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <ContextualHelpButton />
            <NotificationsButton />
            <Divider flexItem orientation="vertical" sx={{ height: 24, mx: 0.5, alignSelf: 'center' }} />
            <UserButton />
          </Stack>
        </Stack>
      </Box>

      <SearchDialog onClose={() => setSearchOpen(false)} open={searchOpen} />

      <MobileNav
        bottomItems={bottomItems}
        items={items}
        onClose={() => setOpenNav(false)}
        open={openNav}
      />
    </React.Fragment>
  );
}

function NotificationsButton(): React.JSX.Element {
  const popover = usePopover<HTMLButtonElement>();
  const { unreadCount } = useNotifications();

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : undefined}
          color="error"
          max={99}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16, px: 0.5 } }}
        >
          <IconButton
            onClick={popover.handleOpen}
            ref={popover.anchorRef}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <BellIcon size={20} />
          </IconButton>
        </Badge>
      </Tooltip>
      <NotificationsPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
      />
    </React.Fragment>
  );
}

function UserButton(): React.JSX.Element {
  const popover = usePopover<HTMLButtonElement>();
  const { user } = useUser();

  return (
    <React.Fragment>
      <Tooltip title="Account">
        <IconButton
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          size="small"
          sx={{ p: 0.25 }}
        >
          <UserAvatar size="32px" user={user} />
        </IconButton>
      </Tooltip>
      <UserPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
      />
    </React.Fragment>
  );
}
